// Vendor Program v2 PR 2 — live Vendor Performance tab, computed from
// borrower sign-offs on monthly reports (replaces the v3.0 placeholder).
// Overall + per-area tiles (x.x / 4), a simple monthly trend (no chart
// lib), the per-customer table, and recent feedback quotes. Semi-annual
// STAFF reviews still ship with Vendor Program v3.0 (footnote below).

import { formatPeriodMonth } from "@/design-system/lib/format";
import { Link } from "react-router-dom";
import { Card } from "@/design-system/components/ui";
import { useVendorPerformance } from "@/hooks/portfolioAdmin/useVendorReports";
import type { VendorDto } from "@/api/portfolioAdmin/types";
import type { VendorPerformanceAreaDto } from "@/api/portfolioAdmin/vendorReportsApi";

interface VendorPerformanceTabProps {
  vendor: VendorDto;
}

function fmtRating(value: number | null | undefined): string {
  return value != null ? `${value.toFixed(1)} / 4` : "—";
}

/**
 * "4 sign-offs — 1 recorded by Phoenix". Staff-recorded sign-offs count toward
 * the averages (the rating is still the borrower's, transcribed), so the tile
 * has to say how many were entered by Phoenix — a blended number with no such
 * marker presents staff-entered ratings as the borrower's own.
 */
function signOffDetail(area: VendorPerformanceAreaDto): string {
  const n = area.signOffCount;
  const base = `${n} sign-off${n === 1 ? "" : "s"}`;
  return area.staffRecordedCount > 0
    ? `${base} — ${area.staffRecordedCount} recorded by Phoenix`
    : base;
}

export function VendorPerformanceTab({ vendor }: VendorPerformanceTabProps) {
  const summaryQuery = useVendorPerformance(vendor.id);

  // Period boundaries are wall-calendar months stored at UTC midnight —
  // rendering them through the tz-aware formatter shifted June into May.
  const month = (iso: string) => formatPeriodMonth(iso, "short");

  if (summaryQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading performance…
        </p>
      </Card>
    );
  }
  if (summaryQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load performance ratings. Refresh to retry.
        </p>
      </Card>
    );
  }

  const summary = summaryQuery.data!;
  const noData = summary.overall.signOffCount === 0;

  return (
    <div
      className="flex flex-col gap-space-4"
      data-testid="vendor-performance-tab"
    >
      <section className="grid grid-cols-2 md:grid-cols-4 gap-space-3">
        <RatingTile
          label="Overall"
          value={summary.overall.overall}
          detail={signOffDetail(summary.overall)}
          testId="vendor-performance-overall"
        />
        <RatingTile
          label="Service quality"
          value={summary.overall.serviceQuality}
          testId="vendor-performance-service-quality"
        />
        <RatingTile
          label="Responsiveness"
          value={summary.overall.responsiveness}
          testId="vendor-performance-responsiveness"
        />
        <RatingTile
          label="Borrower satisfaction"
          value={summary.overall.satisfaction}
          testId="vendor-performance-satisfaction"
        />
      </section>

      {noData ? (
        <Card data-testid="vendor-performance-empty">
          <p className="text-small text-gray-mid m-0">
            No borrower sign-offs yet. Ratings appear once borrowers sign off
            on this vendor&apos;s accepted monthly reports.
          </p>
        </Card>
      ) : (
        <>
          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
              Monthly trend
            </h3>
            {summary.monthlyTrend.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">
                Not enough history for a trend yet.
              </p>
            ) : (
              <ul
                className="m-0 p-0 list-none flex flex-col gap-space-1"
                data-testid="vendor-performance-trend"
              >
                {summary.monthlyTrend.map((point) => (
                  <li
                    key={point.month}
                    className="grid grid-cols-[6rem_1fr_auto] items-center gap-space-2 text-small"
                  >
                    <span className="text-gray-mid">{month(point.month)}</span>
                    <span
                      className="h-2 rounded-pill bg-white-warm overflow-hidden"
                      role="img"
                      aria-label={`${month(point.month)}: ${point.overall.toFixed(1)} out of 4`}
                    >
                      <span
                        className="block h-full bg-gold"
                        style={{ width: `${(point.overall / 4) * 100}%` }}
                      />
                    </span>
                    <span className="tabular-nums text-right">
                      {point.overall.toFixed(1)} / 4
                      <span className="text-caption text-gray-mid">
                        {" "}· {point.count}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
              By customer
            </h3>
            {summary.perCustomer.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">
                No per-customer ratings yet.
              </p>
            ) : (
              <table
                className="w-full text-small border-collapse"
                data-testid="vendor-performance-customers"
              >
                <thead>
                  <tr className="text-left text-caption text-gray-mid uppercase tracking-label">
                    <th className="font-medium pb-space-1">Loan</th>
                    <th className="font-medium pb-space-1">Role</th>
                    <th className="font-medium pb-space-1 text-right">Overall</th>
                    <th className="font-medium pb-space-1 text-right">Service</th>
                    <th className="font-medium pb-space-1 text-right">Responsive</th>
                    <th className="font-medium pb-space-1 text-right">Satisfaction</th>
                    <th className="font-medium pb-space-1 text-right">Sign-offs</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.perCustomer.map((c) => (
                    <tr key={c.loanId} className="border-t border-border-light">
                      <td className="py-space-1 pr-space-2">
                        <Link
                          to={`/portfolio-admin/loans/${c.loanId}?tab=vendors`}
                          className="text-gold-shadow hover:underline"
                        >
                          {c.loanNumber}
                        </Link>
                      </td>
                      <td className="py-space-1">{c.roleOnLoan}</td>
                      <AreaCells ratings={c.ratings} />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card>
            <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
              Recent feedback
            </h3>
            {summary.recentFeedback.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">
                No written feedback yet.
              </p>
            ) : (
              <ul
                className="m-0 p-0 list-none flex flex-col gap-space-2"
                data-testid="vendor-performance-feedback"
              >
                {summary.recentFeedback.map((f, i) => (
                  <li
                    key={`${f.loanNumber}-${f.periodStart}-${i}`}
                    className="border-l-2 border-gold pl-space-3"
                  >
                    <p className="text-small m-0 whitespace-pre-wrap">
                      “{f.feedback}”
                    </p>
                    <p className="text-caption text-gray-mid m-0 mt-space-1">
                      {f.loanNumber} · {month(f.periodStart)} ·{" "}
                      <span className="tabular-nums">
                        {f.overallRating.toFixed(1)} / 4
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}

      <p className="text-caption text-gray-mid italic m-0">
        Ratings come from borrower sign-offs on monthly vendor reports.
        Semi-annual staff performance reviews ship with Vendor Program v3.0.
      </p>
    </div>
  );
}

function RatingTile({
  label,
  value,
  detail,
  testId,
}: {
  label: string;
  value: number | null;
  detail?: string;
  testId: string;
}) {
  return (
    <Card className="flex flex-col gap-space-1" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
        {label}
      </span>
      <span className="font-display font-bold text-h2 tabular-nums">
        {fmtRating(value)}
      </span>
      {detail ? <span className="text-small text-gray-mid">{detail}</span> : null}
    </Card>
  );
}

function AreaCells({ ratings }: { ratings: VendorPerformanceAreaDto }) {
  return (
    <>
      <td className="py-space-1 text-right tabular-nums">
        {fmtRating(ratings.overall)}
      </td>
      <td className="py-space-1 text-right tabular-nums">
        {fmtRating(ratings.serviceQuality)}
      </td>
      <td className="py-space-1 text-right tabular-nums">
        {fmtRating(ratings.responsiveness)}
      </td>
      <td className="py-space-1 text-right tabular-nums">
        {fmtRating(ratings.satisfaction)}
      </td>
      <td className="py-space-1 text-right tabular-nums">
        {ratings.signOffCount}
      </td>
    </>
  );
}
